/**
 * Sistema de caché local usando localStorage
 * Reduce lecturas de Firestore almacenando datos temporalmente
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutos por defecto

/**
 * Guarda datos en caché local
 * @param {string} key - Clave del caché
 * @param {any} data - Datos a guardar
 * @param {number} ttl - Tiempo de vida en milisegundos (opcional)
 */
export const setCache = (key, data, ttl = DEFAULT_TTL) => {
    try {
        const cacheItem = {
            data,
            timestamp: Date.now(),
            ttl
        };
        localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
        return true;
    } catch (error) {
        console.warn('⚠️ Error guardando en caché:', error);
        return false;
    }
};

/**
 * Obtiene datos del caché local
 * @param {string} key - Clave del caché
 * @returns {any|null} - Datos del caché o null si expiró/no existe
 */
export const getCache = (key) => {
    try {
        const cached = localStorage.getItem(`cache_${key}`);
        if (!cached) return null;

        const cacheItem = JSON.parse(cached);
        const now = Date.now();
        const age = now - cacheItem.timestamp;

        // Verificar si el caché expiró
        if (age > cacheItem.ttl) {
            localStorage.removeItem(`cache_${key}`);
            return null;
        }

        return cacheItem.data;
    } catch (error) {
        console.warn('⚠️ Error leyendo caché:', error);
        return null;
    }
};

/**
 * Invalida (elimina) un caché específico
 * @param {string} key - Clave del caché
 */
export const invalidateCache = (key) => {
    try {
        localStorage.removeItem(`cache_${key}`);
        return true;
    } catch (error) {
        console.warn('⚠️ Error invalidando caché:', error);
        return false;
    }
};

/**
 * Invalida todos los cachés que coincidan con un patrón
 * @param {string} pattern - Patrón a buscar (ej: 'rules_')
 */
export const invalidateCachePattern = (pattern) => {
    try {
        const keys = Object.keys(localStorage);
        let count = 0;
        
        keys.forEach(key => {
            if (key.startsWith(`cache_${pattern}`)) {
                localStorage.removeItem(key);
                count++;
            }
        });
        
        console.log(`🗑️ ${count} cachés invalidados con patrón: ${pattern}`);
        return count;
    } catch (error) {
        console.warn('⚠️ Error invalidando cachés:', error);
        return 0;
    }
};

/**
 * Limpia todos los cachés expirados
 */
export const cleanExpiredCache = () => {
    try {
        const keys = Object.keys(localStorage);
        const now = Date.now();
        let cleaned = 0;

        keys.forEach(key => {
            if (key.startsWith('cache_')) {
                try {
                    const cached = localStorage.getItem(key);
                    const cacheItem = JSON.parse(cached);
                    const age = now - cacheItem.timestamp;

                    if (age > cacheItem.ttl) {
                        localStorage.removeItem(key);
                        cleaned++;
                    }
                } catch (e) {
                    // Si hay error parseando, eliminar el caché corrupto
                    localStorage.removeItem(key);
                    cleaned++;
                }
            }
        });

        if (cleaned > 0) {
            console.log(`🗑️ ${cleaned} cachés expirados limpiados`);
        }
        
        return cleaned;
    } catch (error) {
        console.warn('⚠️ Error limpiando cachés:', error);
        return 0;
    }
};

/**
 * Obtiene estadísticas del caché
 */
export const getCacheStats = () => {
    try {
        const keys = Object.keys(localStorage);
        const cacheKeys = keys.filter(k => k.startsWith('cache_'));
        const now = Date.now();
        
        let totalSize = 0;
        let expired = 0;
        let valid = 0;

        cacheKeys.forEach(key => {
            try {
                const cached = localStorage.getItem(key);
                totalSize += cached.length;
                
                const cacheItem = JSON.parse(cached);
                const age = now - cacheItem.timestamp;
                
                if (age > cacheItem.ttl) {
                    expired++;
                } else {
                    valid++;
                }
            } catch (e) {
                expired++;
            }
        });

        return {
            total: cacheKeys.length,
            valid,
            expired,
            sizeKB: (totalSize / 1024).toFixed(2)
        };
    } catch (error) {
        console.warn('⚠️ Error obteniendo stats:', error);
        return { total: 0, valid: 0, expired: 0, sizeKB: 0 };
    }
};

// Limpiar cachés expirados cada 10 minutos
if (typeof window !== 'undefined') {
    setInterval(cleanExpiredCache, 10 * 60 * 1000);
}

const localCache = {
    setCache,
    getCache,
    invalidateCache,
    invalidateCachePattern,
    cleanExpiredCache,
    getCacheStats
};

export default localCache;
